// MUI
import {Box, Divider, Menu, MenuItem, Typography} from '@mui/material';
import {useAuthInfo} from '@propelauth/react';
import PropTypes from 'prop-types';
import React from 'react';
import {useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';

import {OrgSwitcher} from './_OrgSwitcher';
import {UserAvatar} from './_UserAvatar';

export const UserMenu = React.memo(({anchorEl, onClose, onMenuClick}) => {
  const {user, accessHelper} = useAuthInfo();
  const {currentOrg, orgs, hasParentOrg, isExternalUser} = useSelector(
    (state) => state.session
  );
  const parentOrg = orgs?.find((org) => org.orgMetadata?.isParentOrganization);
  const location = useLocation();
  const isParentOrgPage =
    location.pathname === '/parent-organization' || location.pathname === '/';
  const hideOrgSwitcher = hasParentOrg && isParentOrgPage;
  const canManageAccount = !isExternalUser;

  const hasParentOrgAdminAccess =
    parentOrg &&
    (accessHelper.isRole(parentOrg.orgId, 'InScope Admin') ||
      accessHelper.isRole(parentOrg.orgId, 'Owner'));

  const canManageClients =
    hasParentOrgAdminAccess ||
    (parentOrg &&
      accessHelper.hasPermission(parentOrg.orgId, 'can_view_pso_orgs'));

  const handleItemClick = (action) => () => {
    onMenuClick(action);
  };

  if (!user) return null;

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
      disableAutoFocusItem={true}
      id="menu-appbar"
      keepMounted
      onClose={onClose}
      open={Boolean(anchorEl)}
      slotProps={{
        paper: {
          style: {
            border: '1px solid #067a6f',
            borderRadius: '3px',
            boxShadow: '2px 2px 4px 0px rgba(0,0,0,0.25)',
            width: '257px',
          },
        },
      }}
      sx={{mt: '28px'}}
      transformOrigin={{
        horizontal: 'right',
        vertical: 'top',
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          cursor: 'default',
          display: 'flex',
          flexDirection: 'row',
          px: 2,
          py: 1.25,
          userSelect: 'none',
        }}
      >
        <UserAvatar
          bgColor="#DEF1E9"
          size={28}
          textColor="#054A41"
          user={user}
        />
        <Box ml={1}>
          <Typography color="#052E28" fontWeight={700}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <Typography color="#686A6A" fontSize="11px" fontWeight={400}>
            {user?.email}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{borderColor: '#B8D9CC', mx: 1, my: 1}} />

      {hasParentOrgAdminAccess && (
        <MenuItem
          component={Link}
          key="manage-users"
          onClick={handleItemClick('manage-users')}
          sx={{height: 40}}
          to="/settings/organization/users"
        >
          <Typography color="#052E28" fontWeight={400}>
            Manage Users
          </Typography>
        </MenuItem>
      )}

      {canManageClients && (
        <MenuItem
          component={Link}
          key="manage-clients"
          onClick={handleItemClick('manage-clients')}
          sx={{height: 40}}
          to="/settings/organization/clients"
        >
          <Typography color="#052E28" fontWeight={400}>
            Manage Clients
          </Typography>
        </MenuItem>
      )}

      {canManageAccount && (
        <MenuItem
          component={Link}
          key="account-settings"
          onClick={handleItemClick('account')}
          sx={{height: 40}}
          to="/settings"
        >
          <Typography color="#052E28" fontWeight={400}>
            Account Settings
          </Typography>
        </MenuItem>
      )}

      {!hideOrgSwitcher && currentOrg && (
        <>
          <Divider sx={{borderColor: '#B8D9CC', mx: 1, my: 2}} />
          <OrgSwitcher onClose={onClose} />
          <Divider sx={{borderColor: '#B8D9CC', mx: 1, mb: 1, mt: 2}} />
        </>
      )}

      <MenuItem
        key="logout"
        onClick={handleItemClick('logout')}
        sx={{height: 40}}
      >
        <Typography color="#052E28" fontWeight={400}>
          Log out
        </Typography>
      </MenuItem>
    </Menu>
  );
});
UserMenu.displayName = 'UserMenu';
UserMenu.propTypes = {
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onMenuClick: PropTypes.func.isRequired,
};
